// Dependency-free shapefile (.shp/.shx/.dbf/.prj) + ZIP writer for the assigned
// routes. Each route is a GeoJSON LineString/MultiLineString with { trips,
// destination, direction } properties. Pure browser JS (ArrayBuffer/DataView/
// Uint8Array + Blob) — no libraries, so no bundler/Buffer surprises. The core
// builder was verified by a round-trip through shpjs (build zip -> shpjs reads
// it back -> geometry + attributes match).

const SHP_POLYLINE = 3;

// ---- CRC32 (for ZIP) ----
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    t[n] = c >>> 0;
  }
  return t;
})();
function crc32(bytes) {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
}

function ascii(str, len) {
  const out = new Uint8Array(len).fill(0x20);
  let j = 0;
  for (let i = 0; i < str.length && j < len; i++) {
    const code = str.charCodeAt(i);
    out[j++] = code < 128 ? code : 0x3F; // non-ASCII -> '?'
  }
  return out;
}

function featureParts(geom) {
  if (!geom) return [];
  if (geom.type === 'LineString') return [geom.coordinates];
  if (geom.type === 'MultiLineString') return geom.coordinates;
  return [];
}

function buildShpShx(features) {
  let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
  const recs = features.map(parts => {
    let rxmin = Infinity, rymin = Infinity, rxmax = -Infinity, rymax = -Infinity;
    let numPoints = 0;
    for (const part of parts) {
      for (const [x, y] of part) {
        if (x < rxmin) rxmin = x; if (y < rymin) rymin = y;
        if (x > rxmax) rxmax = x; if (y > rymax) rymax = y;
        numPoints++;
      }
    }
    if (rxmin < xmin) xmin = rxmin; if (rymin < ymin) ymin = rymin;
    if (rxmax > xmax) xmax = rxmax; if (rymax > ymax) ymax = rymax;
    const contentBytes = 4 + 32 + 4 + 4 + 4 * parts.length + 16 * numPoints;
    return { parts, rxmin, rymin, rxmax, rymax, numPoints, contentBytes };
  });
  if (!isFinite(xmin)) { xmin = ymin = xmax = ymax = 0; }

  const shpSize = 100 + recs.reduce((s, r) => s + 8 + r.contentBytes, 0);
  const shxSize = 100 + recs.length * 8;
  const shp = new DataView(new ArrayBuffer(shpSize));
  const shx = new DataView(new ArrayBuffer(shxSize));

  function writeHeader(dv, fileWords) {
    dv.setInt32(0, 9994, false);
    dv.setInt32(24, fileWords, false);
    dv.setInt32(28, 1000, true);
    dv.setInt32(32, SHP_POLYLINE, true);
    dv.setFloat64(36, xmin, true);
    dv.setFloat64(44, ymin, true);
    dv.setFloat64(52, xmax, true);
    dv.setFloat64(60, ymax, true);
  }
  writeHeader(shp, shpSize / 2);
  writeHeader(shx, shxSize / 2);

  let off = 100;
  recs.forEach((r, i) => {
    const contentWords = r.contentBytes / 2;
    shp.setInt32(off, i + 1, false);
    shp.setInt32(off + 4, contentWords, false);
    let p = off + 8;
    shp.setInt32(p, SHP_POLYLINE, true); p += 4;
    shp.setFloat64(p, r.rxmin, true); p += 8;
    shp.setFloat64(p, r.rymin, true); p += 8;
    shp.setFloat64(p, r.rxmax, true); p += 8;
    shp.setFloat64(p, r.rymax, true); p += 8;
    shp.setInt32(p, r.parts.length, true); p += 4;
    shp.setInt32(p, r.numPoints, true); p += 4;
    let acc = 0;
    for (const part of r.parts) { shp.setInt32(p, acc, true); p += 4; acc += part.length; }
    for (const part of r.parts) {
      for (const [x, y] of part) { shp.setFloat64(p, x, true); p += 8; shp.setFloat64(p, y, true); p += 8; }
    }
    shx.setInt32(100 + i * 8, off / 2, false);
    shx.setInt32(100 + i * 8 + 4, contentWords, false);
    off += 8 + r.contentBytes;
  });

  return { shp: new Uint8Array(shp.buffer), shx: new Uint8Array(shx.buffer) };
}

function buildDbf(rows, fields) {
  const headerSize = 32 + fields.length * 32 + 1;
  const recordSize = 1 + fields.reduce((s, f) => s + f.len, 0);
  const total = headerSize + rows.length * recordSize + 1;
  const buf = new Uint8Array(total);
  const dv = new DataView(buf.buffer);
  buf[0] = 0x03;
  const now = new Date();
  buf[1] = now.getFullYear() - 1900; buf[2] = now.getMonth() + 1; buf[3] = now.getDate();
  dv.setInt32(4, rows.length, true);
  dv.setInt16(8, headerSize, true);
  dv.setInt16(10, recordSize, true);
  fields.forEach((f, i) => {
    const base = 32 + i * 32;
    for (let k = 0; k < 11; k++) buf[base + k] = k < f.name.length ? f.name.charCodeAt(k) : 0;
    buf[base + 11] = f.type.charCodeAt(0);
    buf[base + 16] = f.len;
    buf[base + 17] = f.dec || 0;
  });
  buf[32 + fields.length * 32] = 0x0D;
  let off = headerSize;
  for (const row of rows) {
    buf[off++] = 0x20;
    for (const f of fields) {
      const val = row[f.name];
      let s;
      if (f.type === 'N') {
        const n = (val === null || val === undefined || val === '') ? 0 : Number(val);
        s = f.dec ? n.toFixed(f.dec) : String(Math.round(n));
        if (s.length > f.len) s = s.slice(0, f.len);
        s = s.padStart(f.len, ' ');
      } else {
        s = (val === null || val === undefined) ? '' : String(val);
        if (s.length > f.len) s = s.slice(0, f.len);
        s = s.padEnd(f.len, ' ');
      }
      buf.set(ascii(s, f.len), off); off += f.len;
    }
  }
  buf[off] = 0x1A;
  return buf;
}

function buildZip(files) {
  const enc = new TextEncoder();
  const parts = [];
  const central = [];
  let offset = 0;
  const u16 = n => new Uint8Array([n & 0xFF, (n >>> 8) & 0xFF]);
  const u32 = n => new Uint8Array([n & 0xFF, (n >>> 8) & 0xFF, (n >>> 16) & 0xFF, (n >>> 24) & 0xFF]);

  for (const { name, data } of files) {
    const nameBytes = enc.encode(name);
    const crc = crc32(data);
    const localBytes = concat([
      u32(0x04034b50), u16(20), u16(0), u16(0), u16(0), u16(0),
      u32(crc), u32(data.length), u32(data.length),
      u16(nameBytes.length), u16(0), nameBytes, data,
    ]);
    parts.push(localBytes);
    central.push(concat([
      u32(0x02014b50), u16(20), u16(20), u16(0), u16(0), u16(0), u16(0),
      u32(crc), u32(data.length), u32(data.length),
      u16(nameBytes.length), u16(0), u16(0), u16(0), u16(0), u32(0),
      u32(offset), nameBytes,
    ]));
    offset += localBytes.length;
  }
  const centralBytes = concat(central);
  const eocd = concat([
    u32(0x06054b50), u16(0), u16(0), u16(files.length), u16(files.length),
    u32(centralBytes.length), u32(offset), u16(0),
  ]);
  return concat([...parts, centralBytes, eocd]);
}

function concat(arrays) {
  let len = 0;
  for (const a of arrays) len += a.length;
  const out = new Uint8Array(len);
  let o = 0;
  for (const a of arrays) { out.set(a, o); o += a.length; }
  return out;
}

const WGS84_PRJ = 'GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137.0,298.257223563]],PRIMEM["Greenwich",0.0],UNIT["Degree",0.0174532925199433]]';

// Build a zipped shapefile (Uint8Array) from GeoJSON route features.
export function buildRoutesShapefileZip(features, baseName = 'assigned_routes') {
  const geoms = [];
  const rows = [];
  for (const f of features) {
    const parts = featureParts(f.geometry).filter(p => p && p.length >= 2);
    if (parts.length === 0) continue;
    geoms.push(parts);
    const p = f.properties || {};
    rows.push({ trips: p.trips, dest: p.destination, direction: p.direction });
  }
  const fields = [
    { name: 'trips', type: 'N', len: 11, dec: 0 },
    { name: 'dest', type: 'C', len: 20, dec: 0 },
    { name: 'direction', type: 'C', len: 12, dec: 0 },
  ];
  const { shp, shx } = buildShpShx(geoms);
  const dbf = buildDbf(rows, fields);
  const enc = new TextEncoder();
  return buildZip([
    { name: `${baseName}.shp`, data: shp },
    { name: `${baseName}.shx`, data: shx },
    { name: `${baseName}.dbf`, data: dbf },
    { name: `${baseName}.prj`, data: enc.encode(WGS84_PRJ) },
  ]);
}

function sanitize(name) {
  return (String(name || 'assigned_routes').replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'assigned_routes').slice(0, 60);
}

// Build the zipped shapefile from assignedNetwork and trigger a browser
// download. Returns { ok, count } (ok=false with reason when there are no
// routes to export). Throws are left to the caller so the UI can surface them.
export function downloadRoutesShapefile(assignedNetwork, baseName = 'assigned_routes') {
  const features = (assignedNetwork && assignedNetwork.routes && assignedNetwork.routes.features) || [];
  const usable = features.filter(f => featureParts(f && f.geometry).some(p => p && p.length >= 2));
  if (usable.length === 0) return { ok: false, reason: 'no routes' };

  const base = sanitize(baseName);
  const zip = buildRoutesShapefileZip(usable, base);
  const blob = new Blob([zip], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${base}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { ok: true, count: usable.length };
}
